import { EQUIPMENT_LABELS } from 'constants/venues/profileOptions';
import {
  CourtSurfacesEnum,
  EquipmentAvailabilitiesEnum,
  VenueLinesEnum,
  VenueNetsEnum,
} from 'types/generated/server';
import CourtFlat from 'svg/CourtFlat';
import PadelRacket from 'svg/PadelRacket';
import Wood from 'svg/Wood';
import { SURFACE_LABELS } from '../../constants';
import type { ClubOfferRow } from '../../types';

// Structural subset so both the club-owner profile (ClubOverview) and the public
// court page (CourtPageV2) venues can be passed in.
interface OffersVenue {
  indoorCourtCount: number;
  outdoorCourtCount: number;
  courtSurfaces: { courtSurface: CourtSurfacesEnum }[];
  equipment: { equipment: EquipmentAvailabilitiesEnum }[];
  hasPickleball?: boolean;
  pickleballNets?: VenueNetsEnum | null;
  pickleballLines?: VenueLinesEnum | null;
}

interface Props {
  venue: OffersVenue;
  /** Renders the per-sport "Court setup:" block (nets • lines). Used by the court page. */
  isShowCourtSetup?: boolean;
}

const NET_LABELS: Record<VenueNetsEnum, string> = {
  [VenueNetsEnum.Permanent]: 'Permanent nets',
  [VenueNetsEnum.Tennis]: 'Shared nets',
  [VenueNetsEnum.Portable]: 'Portable nets',
  [VenueNetsEnum.BringYourOwn]: 'Bring your own nets',
};

const LINE_LABELS: Record<VenueLinesEnum, string> = {
  [VenueLinesEnum.Permanent]: 'Permanent lines',
  [VenueLinesEnum.Tape]: 'Taped lines',
  [VenueLinesEnum.Chalk]: 'Chalk lines',
  [VenueLinesEnum.Temporary]: 'Temporary lines',
};

const ICON_CLASS =
  'h-5 w-5 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon';

export default function ClubOffers({ venue, isShowCourtSetup = false }: Props) {
  const courtsCount = venue.indoorCourtCount + venue.outdoorCourtCount;
  const surfaces = venue.courtSurfaces
    .map((entry) => SURFACE_LABELS[entry.courtSurface])
    .filter(Boolean);
  const equipment = venue.equipment
    .map((entry) => EQUIPMENT_LABELS[entry.equipment])
    .filter(Boolean);

  const rows: ClubOfferRow[] = [];

  if (courtsCount > 0) {
    rows.push({
      key: 'courts',
      label: 'Courts:',
      value: `${venue.indoorCourtCount} indoor • ${venue.outdoorCourtCount} outdoor`,
      icon: <CourtFlat className={ICON_CLASS} />,
    });
  }

  if (surfaces.length > 0) {
    rows.push({
      key: 'surface',
      label: 'Surface:',
      value: surfaces.join(' • '),
      icon: <Wood className={ICON_CLASS} />,
    });
  }

  if (equipment.length > 0) {
    rows.push({
      key: 'equipment',
      label: 'Equipment availability:',
      value: equipment.join(' • '),
      icon: <PadelRacket className={ICON_CLASS} />,
    });
  }

  // Per-sport nets/lines. Only pickleball setup is stored on venues today.
  const courtSetupLines: string[] = [];
  if (isShowCourtSetup && venue.hasPickleball) {
    const setup = [
      venue.pickleballNets && NET_LABELS[venue.pickleballNets],
      venue.pickleballLines && LINE_LABELS[venue.pickleballLines],
    ].filter(Boolean);

    if (setup.length > 0) {
      courtSetupLines.push(`Pickleball: ${setup.join(' • ')}`);
    }
  }

  if (rows.length === 0 && courtSetupLines.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-4">
      <h2 className="typography-product-heading-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
        What club offers:
      </h2>

      <div className="flex flex-col gap-4">
        {rows.map((row) => (
          <div key={row.key} className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              {row.icon}
              <span className="typography-product-card-title-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                {row.label}
              </span>
            </div>
            <span className="typography-product-body text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {row.value}
            </span>
          </div>
        ))}
        {courtSetupLines.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1.5">
              <CourtFlat className={ICON_CLASS} />
              <span className="typography-product-card-title-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Court setup:
              </span>
            </div>
            {courtSetupLines.map((line) => (
              <p
                key={line}
                className="typography-product-body text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
              >
                {line}
              </p>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
